import { BaseEdge, EdgeLabelRenderer, getBezierPath, getSmoothStepPath, type Edge, type EdgeProps } from '@xyflow/react';
import { X } from 'lucide-react';
import { useCustomToolBuilder } from '../builder/builder-context';
import { withoutWire } from './connections';
import type { WireData } from './flow-layout';

const STROKE: Record<WireData['kind'], string> = {
	input: 'var(--color-sky-500)',
	step: 'var(--color-violet-500)',
	after: 'var(--color-gray-600)'
};

/**
 * A value on its way into the argument it fills (from an AI input or another step's field), or a
 * step waiting for another. Clicking it selects it; a selected wire can be removed.
 */
export const WireEdge = ({
	id,
	sourceX,
	sourceY,
	targetX,
	targetY,
	sourcePosition,
	targetPosition,
	data,
	selected,
	markerEnd,
	interactionWidth
}: EdgeProps<Edge<WireData>>) => {
	const { change } = useCustomToolBuilder();
	const points = { sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition };
	const [path, labelX, labelY] = data?.kind === 'after' ? getSmoothStepPath(points) : getBezierPath(points);
	const stroke = STROKE[data?.kind ?? 'step'];
	const lit = data?.emphasis === 'lit';
	const style = {
		stroke,
		strokeWidth: selected ? 2.75 : lit ? 2.25 : 1.75,
		opacity: data?.emphasis === 'faded' ? 0.12 : 1,
		filter: lit ? `drop-shadow(0 0 3px ${stroke})` : undefined,
		transition: 'opacity 150ms, stroke-width 150ms'
	};
	return (
		<>
			<BaseEdge id={id} path={path} markerEnd={markerEnd} style={style} interactionWidth={interactionWidth} />
			{selected && data && (
				<EdgeLabelRenderer>
					<button
						type='button'
						aria-label='Remove this link'
						onClick={() => change((current) => withoutWire(current, data))}
						style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
						className='nodrag nopan pointer-events-auto absolute flex size-5 items-center justify-center rounded-full border border-gray-500 bg-white text-gray-900 shadow-sm hover:bg-red-50 hover:text-red-700'
					>
						<X className='size-3' />
					</button>
				</EdgeLabelRenderer>
			)}
		</>
	);
};
